import React, { useState, useCallback, useEffect, useContext } from 'react';
import Calendar from './CalendarFrame.jsx';
import BookingForm from './BookingForm';
import UpdateForm from './UpdateForm.jsx';
import UserBookings from './UserBookings';
import CancelModal from './CancelModal.jsx';
import ApiService from "../../api/ApiService.js";
import notification from '../../helpers/notification.js';
import moment from 'moment';
import Thankyou from './Thankyou.jsx';
import AppContext from "../../context/AppContext.js";
import GoToTop from '../../helpers/GoToTop.js'

const MAX_PEOPLE_PER_DATE = 4;

// FIX: Use moment for consistent date formatting everywhere
const formatDateString = (date) => {
  return moment(date).format('YYYY-MM-DD');
};

const isPastDate = (date) => {
  const today = moment().startOf('day');
  const checkDate = moment(date).startOf('day');
  return checkDate.isBefore(today);
};

// Custom hooks
const useBookings = () => {
  const [userBookings, setUserBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingsData, setBookingsDataFromBackend] = useState([]);

  const getBookingsCount = useCallback((dateStr) => {
    const bookings = bookingsData[dateStr] || [];

    // Sum the 'people' field only for active bookings
    const count = bookings
      .filter((booking) => booking.status === 'active')
      .reduce((total, booking) => total + (booking.people || 0), 0);

    return count;
  }, [bookingsData]);

  const isDateAvailable = useCallback((dateStr) => {
    return getBookingsCount(dateStr) < MAX_PEOPLE_PER_DATE;
  }, [getBookingsCount]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAnnadaanSevaBookings();
      
      // FIX: Ensure all dates in the response are properly formatted
      const formattedData = {};
      for (const [key, value] of Object.entries(response.data)) {
        const formattedKey = moment(key).format('YYYY-MM-DD');
        formattedData[formattedKey] = value;
      }
      
      setBookingsDataFromBackend(formattedData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // FIX: Ensure date formatting is consistent when adding bookings
  const addBooking = useCallback((dateStr, booking) => {
    // Normalize the date string to YYYY-MM-DD format
    const normalizedDateStr = moment(dateStr).format('YYYY-MM-DD');
    
    setBookingsDataFromBackend(prev => ({
      ...prev,
      [normalizedDateStr]: [...(prev[normalizedDateStr] || []), booking]
    }));
    setUserBookings(prev => [...prev, booking]);
  }, []);

  const cancelBooking = useCallback((booking) => {
    const dateStr = formatDateString(booking.date);

    setBookingsDataFromBackend(prev => ({
      ...prev,
      [dateStr]: prev[dateStr]?.filter(b => b.id !== booking.id) || []
    }));

    setUserBookings(prev => prev.filter(b => b.id !== booking.id));
  }, []);

  return {
    bookingsData,
    userBookings,
    getBookingsCount,
    isDateAvailable,
    addBooking,
    cancelBooking,
    loading,
    refetchBookings: fetchBookings // Add this to manually refetch if needed
  };
};

// Main Component
const AnnadaanSevaRegistration = ({ bookingid, getshowthankyou }) => {
  const { donor } = useContext(AppContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showBookingForm, setShowBookingForm] = useState(true);
  const [showthankyou, setShowthankyou] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [top, setTop] = useState(0);

  const {
    userBookings,
    getBookingsCount,
    isDateAvailable,
    addBooking,
    cancelBooking,
    loading,
    refetchBookings
  } = useBookings();

  const handleMonthChange = useCallback((direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  }, []);

  useEffect(() => {
    if (getshowthankyou) {
      getshowthankyou(showthankyou);
    }
  }, [showthankyou, getshowthankyou]);

  const handleDateSelect = useCallback((date) => {
    if (isPastDate(date)) return;

    const dateStr = formatDateString(date);
    if (!isDateAvailable(dateStr)) return;

    setSelectedDate(date);
    setShowBookingForm(true);
  }, [isDateAvailable]);

  const handleBookingSubmit = useCallback(async (formData) => {
    if (!selectedDate) {
      notification('error', 'Please select a date first');
      return;
    }

    // Basic validation
    if (!formData.fullName || !formData.mobileNo || !formData.address) {
      notification('warning', 'Please fill in all required fields');
      return;
    }

    // FIX: Use consistent date formatting
    const dateStr = formatDateString(selectedDate);
    const currentBookings = getBookingsCount(dateStr);
    console.log('Current bookings for', dateStr, ':', currentBookings);

    const requestedPeople = parseInt(formData.numberOfPeople);

    if (currentBookings + requestedPeople > MAX_PEOPLE_PER_DATE) {
      notification('warning', `Only ${MAX_PEOPLE_PER_DATE - currentBookings} slots available for this date`);
      return;
    }

    // Create new booking object to send to backend
    const newBooking = {
      fullName: formData.fullName,
      mobileNo: formData.mobileNo,
      age: formData.age,
      emailId: formData.emailId,
      address: formData.address,
      profession: formData.profession,
      eduQualif: formData.eduQualif,
      orgName: formData.orgName,
      jobTitle: formData.jobTitle,
      numberOfPeople: requestedPeople,
      date: dateStr // Already formatted as YYYY-MM-DD
    };

    try {
      const response = await ApiService.registerAnnadaanSeva(newBooking);

      // FIX: After successful booking, refetch data from backend
      // This ensures we have the latest booking counts
      await refetchBookings();

      setSelectedDate(null);
      setShowBookingForm(false);
      setShowthankyou(true);
      setTop(150);
      notification("success", 'Booking confirmed successfully!');

    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 400) {
        const message = error.response.data?.message || 'Duplicate booking or validation error.';
        notification('error', message);
      } else {
        notification('error', 'An error occurred while submitting the booking. Please try again later.');
      }
    }
  }, [selectedDate, getBookingsCount, refetchBookings]);

  const handleCancelBooking = useCallback((booking) => {
    cancelBooking(booking);
    setBookingToCancel(null);
    alert('Booking cancelled successfully!');
  }, [cancelBooking]);

  const closeBookingForm = useCallback(() => {
    setShowBookingForm(false);
    setSelectedDate(null);
  }, []);

  const festRef = useCallback((node) => {
    if (node) {
      setTop(node.offsetTop - 230);
    }
  }, []);

  return (
    <>
      <div className="min-vh-100 bg-light">
        <div className="container-lg">
          {/* Header */}
          {showthankyou ? "" :
            <div className="bg-warning text-white rounded-top p-2 text-center">
              <div className="d-flex justify-content-center align-items-center">
                <h1 className="h4 fw-bold">Annadaan Distribution Seva</h1>
              </div>
            </div>
          }
          
          {/* Main Content */}
          <div className="bg-white rounded-bottom p-3">
            <div className="row" ref={festRef}>
              {/* Booking Form Section */}
              {bookingid ?
                <div className="col-12">
                  {showBookingForm && selectedDate && (
                    <UpdateForm
                      selectedDate={selectedDate}
                      onSubmit={handleBookingSubmit}
                      onClose={closeBookingForm}
                      getBookingsCount={getBookingsCount}
                      currentDate={currentDate}
                      handleDateSelect={handleDateSelect}
                      handleMonthChange={handleMonthChange}
                      isDateAvailable={isDateAvailable}
                      id={bookingid}
                      phone={donor?.phone}
                    />
                  )}
                </div>
                :
                <div className="col-12">
                  {showBookingForm && selectedDate && (
                    <BookingForm
                      selectedDate={selectedDate}
                      onSubmit={handleBookingSubmit}
                      onClose={closeBookingForm}
                      getBookingsCount={getBookingsCount}
                      currentDate={currentDate}
                      handleDateSelect={handleDateSelect}
                      handleMonthChange={handleMonthChange}
                      isDateAvailable={isDateAvailable}
                    />
                  )}
                </div>
              }

              {showthankyou ? <Thankyou set={top} /> : ''}
            </div>
          </div>

          {/* Cancel Booking Modal */}
          <CancelModal
            booking={bookingToCancel}
            onConfirm={handleCancelBooking}
            onClose={() => setBookingToCancel(null)}
          />
        </div>
      </div>
      <GoToTop top={top} />
    </>
  );
};

export default AnnadaanSevaRegistration;